import { DurationFormat, DurationInput } from "@formatjs/intl-durationformat"
import {
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal,
} from "@internationalized/date"
import { Pass } from "ipctg-inflow-types/InFlowDB"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import { formatDateForDisplay, getLocale, prettyPassID } from "@/utilities/etc"
import { useTheme } from "@/utilities/hooks/useTheme"
import { getPassSessionLengthCopy } from "@/utilities/passes"

import styles from "./index.module.css"

export function PurchasedQuotaPackCard(
  props: Pass & {
    action: "redeem" | "warn" | "info" | "viewDetails" | "none"
    isPending: boolean
    onActionPressed: () => void
  }
) {
  const { theme, locationInformation } = useTheme()
  const cardBackground = theme?.components?.molecules?.flexCard?.background
  const { t, i18n } = useTranslation("passes")
  const redemptionConfig = props.passSpecifications?.redemptionConfig
  const redeemCopy = getPassSessionLengthCopy({
    redemptionConfig,
    locale: i18n.resolvedLanguage,
    t,
    mode: "redeem",
    timeZone: locationInformation?.timeZone,
  })

  return (
    <article
      className={styles.productCard}
      data-type="quotaPack"
      style={{
        background: cardBackgroundOverride(),
      }}
    >
      <div className={styles.purchasedCardHeader}>
        <Icon name="ticket" />
        <Heading level={2} className="body--3 bold">
          {t("purchasedProducts.quotaPack.label")}
        </Heading>
        <Text level={3}>{prettyPassID(props.passID)}</Text>
      </div>
      <div>
        <Heading level={4}>{props.passName}</Heading>
        {parseAbsoluteToLocal(props.validTo).compare(now(getLocalTimeZone())) <=
        0 ? (
          <Text level={3} className="bold">
            {t("purchasedProducts.quotaPack.expired")}
          </Text>
        ) : props.uses >= props.maxUses ? (
          <Text level={3} className="bold">
            {t("purchasedProducts.quotaPack.maxed")}
          </Text>
        ) : (
          <Text
            level={3}
            className="bold"
            style={{ marginTop: "var(--space-1)" }}
          >
            {t("purchasedProducts.quotaPack.passes", {
              count: props.maxUses - props.uses,
            })}
            {" · "}
            {timeLeft()}
          </Text>
        )}
        {props.action === "redeem" ? (
          <Button
            forced={
              theme?.components?.molecules?.flexCard?.button || {
                hue: "neutral",
                variant: "light",
              }
            }
            onPress={() => props.onActionPressed()}
            isPending={props.isPending}
          >
            {redeemCopy}
          </Button>
        ) : props.action === "viewDetails" ? (
          <Button
            forced={
              theme?.components?.molecules?.flexCard?.button || {
                hue: "neutral",
                variant: "light",
              }
            }
            onPress={() => props.onActionPressed()}
            isPending={props.isPending}
          >
            {t("purchasedProducts.quotaPack.viewMoreInfo")}
          </Button>
        ) : props.action === "warn" ? (
          <div className="warning-card">
            <Icon name="alert" />
            <Text level={3}>
              {t("purchasedProducts.quotaPack.redeem.cannotRedeem")}
            </Text>
          </div>
        ) : (
          <></>
        )}
      </div>
    </article>
  )

  function timeLeft() {
    const diffMs = parseAbsoluteToLocal(props.validTo).compare(
      now(getLocalTimeZone())
    )

    if (diffMs < 0) {
      return ""
    }

    const totalMinutes = Math.max(0, Math.round(diffMs / 60_000))

    const minutesInDay = 24 * 60
    const minutesInHour = 60

    let duration: DurationInput
    if (totalMinutes >= 30 * minutesInDay) {
      /*
        Show date 30 days out

        This could cause CX confusion as it is in the
        user's local time zone, not the location's which
        could cause the end date to be offset +/- 1 day.
        We will only show this if its more than 30 days out
        so hopefuly they don't remember but all the other
        day/times are in the user's local time zone and its
        1am rn so I can't be arsed to do anything more than
        write this comment.
      */
      return t("purchasedProducts.quotaPack.validUntil", {
        date: formatDateForDisplay({
          isoDateString: props.validTo,
          locale: i18n.resolvedLanguage || getLocale(),
        }),
      })
    } else if (totalMinutes >= 2 * minutesInDay) {
      // Days only
      duration = {
        days: Math.floor(totalMinutes / minutesInDay),
      }
    } else if (totalMinutes >= minutesInDay) {
      // Days + hours
      const days = Math.floor(totalMinutes / minutesInDay)
      const hours = Math.floor((totalMinutes % minutesInDay) / minutesInHour)

      duration = { days, hours }
    } else if (totalMinutes >= minutesInHour) {
      // Hours only
      duration = {
        hours: Math.floor(totalMinutes / minutesInHour),
      }
    } else {
      // Minutes only
      duration = {
        minutes: totalMinutes,
      }
    }

    return t("purchasedProducts.quotaPack.validFor", {
      time: new DurationFormat(i18n.resolvedLanguage || getLocale(), {
        style: "short",
      }).format(duration),
    })
  }

  function cardBackgroundOverride() {
    if (cardBackground) {
      return `url("@/assets/effects/noise.png") 0% 0% / 500px 500px repeat, linear-gradient(${cardBackground?.deg}, ${cardBackground?.colors.map((c) => `var(--${c.hue}-${c.step})`).join(", ")})`
    }
  }
}

export function OnDemandPurchasedCard() {
  return <></>
}
